import { useEffect, useState } from "react";
import { PolicyParameterValueI } from "../../../interfaces/business";
import OverlaySpinner from "../../OverlaySpinner";
import { Toast } from "../../Base/toast";
import {
  fetchBusinessParameter,
  updateBusinessParameter,
} from "../../../features/business/business";
import { deepCompareObjects } from "../../../utils";
import DepositPolicy from "./DepositPolicy";
import AppointmentPolicy from "./AppointmentPolicy";

const BusinessPolicy = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [initData, setInitData] = useState<PolicyParameterValueI>({
    policyNotes: [],
    appointmentPolicy: [],
  });
  const [formData, setFormData] = useState<PolicyParameterValueI>({
    policyNotes: [],
    appointmentPolicy: [],
  });

  useEffect(() => {
    handleDataFetch();
  }, []);

  useEffect(() => {
    if (initData) {
      setFormData(initData);
    }
  }, [initData]);

  const handleDataFetch = async () => {
    setIsLoading(true);
    try {
      const data = await fetchBusinessParameter<PolicyParameterValueI>(
        "policy"
      );
      if (data) {
        setInitData(data);
      }
    } catch (error) {
      if ((error as any).message) {
        Toast((error as any).message, "error");
      }
      Toast("Something Went Wrong, Please Kindly Refresh Page", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deepCompareObjects(formData, initData)) {
      return;
    }

    setIsLoading(true);
    try {
      await updateBusinessParameter("policy", formData);
      Toast("Settings updated successfully", "success");
      setInitData(formData);
    } catch (error) {
      Toast("Failed to update settings", "error");
      console.error("Update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(initData);
    Toast("Changes discarded", "info");
  };

  const handlePolicyNoteCreation = (payload: {
    title: string;
    desc: string;
  }) => {
    setFormData({
      ...formData,
      policyNotes: [...formData.policyNotes, payload],
    });
  };

  const handlePolicyNoteUpdate = (
    index: number,
    field: "title" | "desc",
    value: string
  ) => {
    setFormData({
      ...formData,
      policyNotes: formData.policyNotes.map((note, idx) =>
        idx === index ? { ...note, [field]: value } : note
      ),
    });
  };

  const handlePolicyNoteRemoval = (index: number) => {
    setFormData({
      ...formData,
      policyNotes: formData.policyNotes.filter((_, idx) => idx !== index),
    });
  };

  const handleAppointmentPolicyCreation = (desc: string) => {
    setFormData({
      ...formData,
      appointmentPolicy: [...formData.appointmentPolicy, desc],
    });
  };

  const handleAppointmentPolicyUpdate = (index: number, value: string) => {
    setFormData({
      ...formData,
      appointmentPolicy: formData.appointmentPolicy.map((policy, idx) =>
        idx === index ? value : policy
      ),
    });
  };

  const handleAppointmentPolicyRemoval = (index: number) => {
    setFormData({
      ...formData,
      appointmentPolicy: formData.appointmentPolicy.filter(
        (_, idx) => idx !== index
      ),
    });
  };

  return (
    <div className="flex justify-center p-4">
      <div className="md:p-12 rounded-xl shadow-lg border-2 border-slate-600 w-full flex flex-col gap-4">
        <span className="text-lg font-bold font-outfit">
          Deposit & Apppointment Policy
        </span>
        <OverlaySpinner isLoading={isLoading}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            <div className="flex flex-col gap-8">
              <span className="font-outfit text-lg font-bold">Policies</span>
              {/* Render Previousl Created Policy */}

              {formData.policyNotes.map((policyNote, key) => {
                return (
                  <DepositPolicy
                    key={key}
                    initData={policyNote}
                    isLastEntry={key === formData.policyNotes.length}
                    updateDepositPolicy={handlePolicyNoteUpdate}
                    removeDepositPolicy={handlePolicyNoteRemoval}
                    index={key}
                  />
                );
              })}
              {/* Render Deposit Policy Component to add current policy to existing list and clear form  */}
              <DepositPolicy addDepositPolicy={handlePolicyNoteCreation} />
            </div>
            <div className="flex flex-col gap-4">
              <span className="font-outfit text-lg font-bold">
                Before Your Appointment
              </span>

              {formData.appointmentPolicy.map((ap, index) => {
                return (
                  <AppointmentPolicy
                    key={index}
                    initData={ap}
                    isLastEntry={index === formData.appointmentPolicy.length}
                    updateAppointmentPolicy={handleAppointmentPolicyUpdate}
                    removeAppointmentPolicy={handleAppointmentPolicyRemoval}
                    index={index}
                  />
                );
              })}

              <AppointmentPolicy
                addAppointmentPolicy={handleAppointmentPolicyCreation}
              />
            </div>

            <div className="flex gap-6 font-outfit font-bold text-sm justify-start">
              <button
                type="submit"
                disabled={deepCompareObjects(formData, initData)}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  deepCompareObjects(formData, initData)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-900 hover:bg-blue-800 text-white"
                }`}
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={deepCompareObjects(formData, initData)}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  deepCompareObjects(formData, initData)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-900 hover:bg-red-800 text-white"
                }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </OverlaySpinner>
      </div>
    </div>
  );
};

export default BusinessPolicy;
